/**
 * business.ts — the Business Operating Domain component library (typed).
 *
 * The `generative-canvas` primitive used to be built from GENERIC GenUI blocks
 * (group + fields). That under-constrained the answer: a Value Proposition Canvas
 * assembled from loose key/value rows neither reads as a canvas nor enforces the
 * relationships that make one (a pain reliever RELIEVES a pain; a value proposition
 * SERVES a customer segment). This module replaces that with a small, typed,
 * COMPOSABLE vocabulary for the business operating domain — same arktype triple-duty
 * the rest of the domain enjoys (validate · infer · `.toJsonSchema()`).
 *
 * The shape mirrors Osterwalder's Business Model Canvas + Value Proposition Canvas:
 *
 *   • A {@link CustomerSegment} (an ICP) owns its jobs / pains / gains.
 *   • A {@link ValueProposition} SERVES one or more segments and its pain relievers /
 *     gain creators REFERENCE the pains / gains they address (`addresses` ids).
 *   • A {@link BusinessModel} composes the nine canvas areas; every area entry may map
 *     to the segments it applies to (`segmentIds`), so the full business can be viewed
 *     whole (colour-coded, links highlighted) OR filtered to one segment's subset.
 *
 * Cross-references are by `id` WITHIN one model — entries are shared/linked across the
 * top-level areas (a segment's pain id is referenced by a value map's reliever), which
 * is exactly the "components reference/link each other" the library is built around.
 */
import { type } from "arktype";

/**
 * Segment accent palette size. Each segment is assigned a `tone` 0..N-1 that maps to a
 * `--seg-{n}` CSS variable (defined for both themes in app.css). Colour is how a human
 * sees which entries across the whole canvas belong to which customer segment.
 */
export const SEGMENT_TONE_COUNT = 6;

/** Where an entry sits in the elicitation lifecycle (drives the answer-canvas affordances). */
export const ENTRY_STATES = ["filled", "open", "candidate"] as const;
export type EntryState = (typeof ENTRY_STATES)[number];

/**
 * One atom of business content (a job, a pain, a reliever, a revenue line…). It carries
 * a model-local `id` so other entries can link to it, the segments it applies to, and an
 * optional elicitation `state`: `open` is a blank to fill, `candidate` is AI-proposed and
 * awaits the human's accept/grade, `filled` is confirmed. `addresses` links this entry to
 * the ids it resolves (a pain reliever → the pain ids it relieves).
 */
export const CanvasEntry = type({
  id: "string",
  text: "string",
  /** Customer segments this entry applies to (empty/absent ⇒ the whole business). */
  "segmentIds?": "string[]",
  /** Entries elsewhere in the model this one addresses/links to (reliever → pain ids). */
  "addresses?": "string[]",
  /** Elicitation lifecycle; absent ⇒ a plain filled entry. */
  "state?": "'filled' | 'open' | 'candidate'",
  /** Human grade (1–5) when the task is to prioritise; absent ⇒ ungraded. */
  "grade?": "number",
});
export type CanvasEntry = typeof CanvasEntry.infer;

/**
 * A customer segment / ICP — the unit the library supports MANY of (not a single ICP).
 * It owns the Customer Profile side of a Value Proposition Canvas: jobs, pains, gains.
 */
export const CustomerSegment = type({
  id: "string",
  name: "string",
  /** Accent index 0..SEGMENT_TONE_COUNT-1 → `--seg-{tone}`. */
  "tone?": "number",
  /** A one-liner on who this is (e.g. "Heads of AI governance at regulated firms"). */
  "profile?": "string",
  jobs: CanvasEntry.array(),
  pains: CanvasEntry.array(),
  gains: CanvasEntry.array(),
});
export type CustomerSegment = typeof CustomerSegment.infer;

/**
 * A value proposition — the Value Map side. It SERVES one or more segments (`segmentIds`)
 * and its pain relievers / gain creators REFERENCE (via each entry's `addresses`) the
 * pain / gain ids they resolve, so fit can be drawn as real links rather than implied.
 */
export const ValueProposition = type({
  id: "string",
  name: "string",
  /** The customer segments this proposition is aimed at. */
  segmentIds: "string[]",
  products: CanvasEntry.array(),
  painRelievers: CanvasEntry.array(),
  gainCreators: CanvasEntry.array(),
});
export type ValueProposition = typeof ValueProposition.infer;

/**
 * The composed business model — the nine Business Model Canvas areas. Customer Segments
 * and Value Propositions are first-class typed sub-components (above); the remaining
 * seven areas are entry lists whose `segmentIds` map each line to the segment(s) it
 * serves, so the canvas filters and colour-codes per segment.
 */
export const BusinessModel = type({
  "title?": "string",
  segments: CustomerSegment.array(),
  valuePropositions: ValueProposition.array(),
  channels: CanvasEntry.array(),
  customerRelationships: CanvasEntry.array(),
  revenueStreams: CanvasEntry.array(),
  keyResources: CanvasEntry.array(),
  keyActivities: CanvasEntry.array(),
  keyPartnerships: CanvasEntry.array(),
  costStructure: CanvasEntry.array(),
});
export type BusinessModel = typeof BusinessModel.infer;

/**
 * The GenUI block that hosts a whole {@link BusinessModel}. Adding it to the block
 * vocabulary means a business canvas composes into the scratchpad AND is the answer
 * surface of the `generative-canvas` primitive — one typed component, rendered the same
 * everywhere. `view` chooses the default lens (the full nine-area model, or the zoomed
 * Value Proposition Canvas); a human can switch lenses in the UI regardless.
 */
export const BusinessCanvasBlock = type({
  kind: "'business-canvas'",
  "title?": "string",
  "view?": "'model' | 'value-proposition'",
  model: BusinessModel,
});
export type BusinessCanvasBlock = typeof BusinessCanvasBlock.infer;

/** The seven entry-list areas, in canonical Business Model Canvas reading order. */
export const MODEL_AREA_KEYS = [
  "keyPartnerships",
  "keyActivities",
  "keyResources",
  "customerRelationships",
  "channels",
  "revenueStreams",
  "costStructure",
] as const;
export type ModelAreaKey = (typeof MODEL_AREA_KEYS)[number];

/** Display labels for every area, so the renderer never hard-codes prose. */
export const AREA_LABEL: Readonly<Record<string, string>> = {
  keyPartnerships: "Key Partnerships",
  keyActivities: "Key Activities",
  keyResources: "Key Resources",
  valuePropositions: "Value Propositions",
  customerRelationships: "Customer Relationships",
  channels: "Channels",
  segments: "Customer Segments",
  costStructure: "Cost Structure",
  revenueStreams: "Revenue Streams",
};

/** Glyphs (outline Unicode, never SVG chrome) that label each area at a glance. */
export const AREA_GLYPH: Readonly<Record<string, string>> = {
  keyPartnerships: "⛓",
  keyActivities: "⚙",
  keyResources: "◈",
  valuePropositions: "✦",
  customerRelationships: "♡",
  channels: "⇄",
  segments: "◉",
  costStructure: "▽",
  revenueStreams: "△",
};

/** A short, deterministic id for a freshly added entry/segment (no Date.now/random). */
export function entryId(prefix: string, seed: string, salt: number): string {
  const base = seed.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24);
  return `${prefix}-${base || "x"}-${salt.toString(36)}`;
}

// ── Typed evidence for the other business-operating-model methods ────────────────
// Each business-pack instrument emits a STRUCTURED, typed answer (not a free-text blob),
// so the operating model is captured as data the rest of the pipeline can reason over —
// the same "typed component per method" principle as the business-canvas, applied to the
// whole pack. These validate the widget's `rawContent` and infer its TS shape.

/** artifact-walkthrough → an annotated artifact: each part labelled with its purpose and
 *  whether it lands, confuses, or is missing (the signal a sales/ops artifact actually sends). */
export const ArtifactPart = type({
  id: "string",
  part: "string",
  purpose: "string",
  "signal?": "'works' | 'confuses' | 'missing'",
});
export type ArtifactPart = typeof ArtifactPart.infer;
export const ArtifactWalkthrough = type({
  artifact: "string",
  "context?": "string",
  parts: ArtifactPart.array(),
});
export type ArtifactWalkthrough = typeof ArtifactWalkthrough.infer;

/** contextual-inquiry → a workflow / handoff map: an ordered chain of steps, each with its
 *  actor, action, the tool used, the handoff to the next owner, and the risk lurking there. */
export const WorkflowStep = type({
  id: "string",
  "actor?": "string",
  action: "string",
  "tool?": "string",
  "handoff?": "string",
  "risk?": "string",
});
export type WorkflowStep = typeof WorkflowStep.infer;
export const WorkflowMap = type({
  "trigger?": "string",
  steps: WorkflowStep.array(),
});
export type WorkflowMap = typeof WorkflowMap.infer;

/** critical-incident → a structured operational incident with its outcome, the segment it
 *  touched, and the lesson — so an anecdote becomes evidence with a clear boundary. */
export const OperationalIncident = type({
  situation: "string",
  action: "string",
  outcome: "string",
  "rationale?": "string",
  "segment?": "string",
  "lesson?": "string",
});
export type OperationalIncident = typeof OperationalIncident.infer;

/** self-explanation → a rationale: the choice, the reasons behind it, the assumptions it
 *  rests on, and the one thing that would change the answer (a disconfirmer guards post-hoc). */
export const Rationale = type({
  choice: "string",
  reasons: "string[]",
  "assumptions?": "string[]",
  "disconfirmer?": "string",
});
export type Rationale = typeof Rationale.infer;

/** scenario-vignette → a business decision: the chosen option, who it affects, the trade-off
 *  it accepts, and a caveat that must travel with it. */
export const BusinessDecision = type({
  option: "string",
  decision: "string",
  "affects?": "string",
  "tradeoff?": "string",
  "caveat?": "string",
});
export type BusinessDecision = typeof BusinessDecision.infer;

/** card-sort (business) → decision rights: each case assigned to who owns the call. */
export const DecisionRights = type({
  lanes: type({ id: "string", label: "string" }).array(),
  assignments: "Record<string, string[]>",
});
export type DecisionRights = typeof DecisionRights.infer;

/** An empty business model — the starter the editor and the synthetic provider build on. */
export function emptyModel(title?: string): BusinessModel {
  return {
    ...(title ? { title } : {}),
    segments: [],
    valuePropositions: [],
    channels: [],
    customerRelationships: [],
    revenueStreams: [],
    keyResources: [],
    keyActivities: [],
    keyPartnerships: [],
    costStructure: [],
  };
}
